
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Calendar, PhoneCall } from 'lucide-react';
import { motion } from 'framer-motion';

export default function HoursSection() {
  const { t } = useTranslation();

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <section className="py-24 bg-raya-section relative overflow-hidden">
      {/* Decorative Accents */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-raya-accent/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-raya-accent/5 rounded-full blur-3xl pointer-events-none"></div>

      <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        >
          {/* Centered Header */}
          <div className="text-center mb-12">
            <motion.span 
              variants={itemVariants} 
              className="inline-block py-1.5 px-4 rounded-full bg-raya-accent text-black text-xs md:text-sm font-bold mb-6 shadow-sm"
            >
              🕒 {t('hours.title')}
            </motion.span>
            <motion.h2 
              variants={itemVariants} 
              className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight text-raya-text-primary"
            >
              {t('hours.planVisit')}
            </motion.h2>
            <motion.p 
              variants={itemVariants}
              className="text-lg text-raya-text-primary/80 max-w-2xl mx-auto leading-relaxed"
            >
              {t('hours.description')}
            </motion.p>
          </div>

          {/* Content Row */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-12 lg:gap-20">
            
            {/* Hours List */}
            <div className="flex-1 space-y-6 w-full">
              {/* Weekdays */}
              <motion.div variants={itemVariants} className="flex items-center gap-4 group">
                <div className="p-3 bg-raya-accent/10 rounded-2xl group-hover:bg-raya-accent/20 transition-colors">
                  <Clock className="w-6 h-6 text-raya-accent" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-raya-text-primary">{t('hours.weekdays')}</h3>
                  <p className="font-bold text-raya-accent">11:00 AM - 9:00 PM</p>
                </div>
              </motion.div>

              {/* Weekends */}
              <motion.div variants={itemVariants} className="flex items-center gap-4 group">
                <div className="p-3 bg-raya-accent/10 rounded-2xl group-hover:bg-raya-accent/20 transition-colors">
                  <Calendar className="w-6 h-6 text-raya-accent" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-raya-text-primary">{t('hours.weekends')}</h3>
                  <p className="font-bold text-raya-accent">10:00 AM - 8:00 PM</p>
                </div>
              </motion.div>

              {/* On Call */}
              <motion.div variants={itemVariants} className="flex items-center gap-4 group">
                 <div className="p-3 bg-raya-accent/10 rounded-2xl group-hover:bg-raya-accent/20 transition-colors">
                  <PhoneCall className="w-6 h-6 text-raya-accent" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-raya-text-primary">{t('hours.onCall')}</h3>
                  <p className="font-bold text-raya-accent">{t('hours.flexible')}</p>
                </div>
              </motion.div>
            </div>

            {/* Call to Action Card */}
            <motion.div 
              variants={itemVariants}
              className="w-full md:w-auto md:min-w-[320px]"
            >
              <div className="bg-gradient-to-br from-raya-accent to-raya-accent-hover rounded-3xl p-8 text-center text-black shadow-2xl relative overflow-hidden group">
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Decorative Pattern in Card */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12 transition-transform duration-700 group-hover:scale-150"></div>
                
                <div className="relative z-10 flex flex-col items-center">
                  <div className="w-16 h-16 bg-black/10 rounded-full flex items-center justify-center mb-6">
                    <PhoneCall className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{t('hours.callUs')}</h3>
                  <a 
                    href="tel:+18438470158" 
                    className="block text-3xl font-extrabold tracking-tight hover:underline transition-all"
                  >
                    +1 (843) 847-0158
                  </a>
                  <div className="mt-6 pt-6 border-t border-black/10 w-full text-sm font-bold opacity-80 uppercase tracking-widest">
                    {t('hours.area')}
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </motion.div>
    </section>
  );
}
